import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Users2, FileText, Star, Library, LogIn, Sparkles, Plug, Hash, KanbanSquare, ChevronLeft, Settings, Search, ImageOff, ClipboardList, AlertTriangle, ListChecks } from "lucide-react";
import SettingsTile from "@/components/SettingsTile";
import UsersTab from "@/pages/admin/UsersTab";
import TestimonialsTab from "@/pages/admin/TestimonialsTab";
import LibraryTab from "@/pages/admin/LibraryTab";
import ImageResyncTab from "@/pages/admin/ImageResyncTab";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ProposalEmailTemplates from "@/components/ProposalEmailTemplates";
import ReminderScheduleCard from "@/components/ReminderScheduleCard";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Pencil, KeyRound, UserCheck, UserX, Eye, Mail } from "lucide-react";
import DocumentEditor from "@/components/DocumentEditor";
import RichText from "@/components/RichText";
import LoEAdmin from "@/components/LoEAdmin";
import TemplatesManager from "@/components/TemplatesManager";
import HomeAdmin from "@/components/HomeAdmin";
import WelcomeAdmin from "@/components/WelcomeAdmin";
import XeroCard from "@/components/XeroCard";
import NumberingCard from "@/components/NumberingCard";
import AnnualUpliftCard from "@/components/AnnualUpliftCard";
import ImagePicker from "@/components/ImagePicker";
import { confirmDialog } from "@/lib/dialogs";
const BK = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isPricingAdmin = ["super_admin", "pricing_admin", "approver"].includes(user?.role);
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = sp.get("tab");
  const [settingsQuery, setSettingsQuery] = useState("");
  const [content, setContent] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [liveUrl, setLiveUrl] = useState(null);
  const [library, setLibrary] = useState([]);
  const [nl, setNl] = useState({ category: "founder", name: "", html: "" });
  const [editLib, setEditLib] = useState(null);
  const [nt, setNt] = useState({ quote: "", author: "", role: "", industry: "", image_path: "", image_url: "", logo_path: "", logo_url: "" });
  const [pick, setPick] = useState(null); // { kind: "image"|"logo", id: "new"|<tid> }
  const [dragIdx, setDragIdx] = useState(null);
  const [emailLogo, setEmailLogo] = useState("");
  const [emailPick, setEmailPick] = useState(false);
  useEffect(() => { api.get("/brand").then((r) => setEmailLogo(r.data?.email_logo_path || "")).catch(() => {}); }, []);
  const [editT, setEditT] = useState(null); // { id, quote, author, role, industry }
  const startEdit = (t) => setEditT({ id: t.id, quote: t.quote || "", author: t.author || "", role: t.role || "", industry: t.industry || "" });
  const saveEdit = async () => {
    try {
      const { id, ...fields } = editT;
      await api.put(`/testimonials/${id}`, fields);
      setEditT(null); reloadT(); toast.success("Testimonial updated");
    } catch { toast.error("Could not save"); }
  };
  useEffect(() => {
    const xq = new URLSearchParams(window.location.search).get("xero");
    if (xq === "connected") toast.success("Xero connected");
    if (xq === "error") toast.error("Xero connection failed — check your keys and redirect URI");
    api.get("/content").then((r) => setContent(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/email-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/content-library").then((r) => setLibrary(r.data)).catch(() => {});
  }, [isSuper]);

  const reloadT = () => api.get("/testimonials").then((r) => setTestimonials(r.data));
  const createTestimonial = async () => {
    if (!nt.quote.trim() && !nt.image_path) { toast.error("Add a quote and/or an image"); return; }
    try {
      await api.post("/testimonials", nt);
      setNt({ quote: "", author: "", role: "", industry: "", image_path: "", image_url: "", logo_path: "", logo_url: "" });
      reloadT(); toast.success("Testimonial added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const delTestimonial = async (id) => {
    if (!(await confirmDialog({ title: "Delete testimonial?", description: "This testimonial will be permanently removed.", tone: "destructive", confirmText: "Delete" }))) return;
    await api.delete(`/testimonials/${id}`); reloadT(); toast.success("Testimonial deleted");
  };
  const toggleTestimonial = async (t) => { await api.put(`/testimonials/${t.id}`, { active: !t.active }); reloadT(); };
  const dropTestimonial = async (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    const list = [...testimonials];
    const [moved] = list.splice(dragIdx, 1);
    list.splice(i, 0, moved);
    setTestimonials(list); setDragIdx(null);
    try { await api.post("/testimonials/reorder", { ids: list.map((x) => x.id) }); toast.success("Order saved"); }
    catch { toast.error("Could not save order"); reloadT(); }
  };
  const handlePick = async (path, url) => {
    const p = pick; setPick(null);
    if (!p) return;
    if (p.kind === "email_logo") {
      try { await api.put("/brand", { email_logo_path: path }); setEmailLogo(path); toast.success("Email logo updated"); }
      catch { toast.error("Could not update"); }
      return;
    }
    const field = p.kind === "logo" ? "logo" : "image";
    if (p.id === "new") { setNt((s) => ({ ...s, [`${field}_path`]: path, [`${field}_url`]: url })); return; }
    try { await api.put(`/testimonials/${p.id}`, { [`${field}_path`]: path }); reloadT(); toast.success(`${field === "logo" ? "Logo" : "Image"} updated`); }
    catch { toast.error("Could not update"); }
  };
  const removeLogo = async (t) => { await api.put(`/testimonials/${t.id}`, { logo_path: "" }); reloadT(); };
  const previewSample = async () => {
    try {
      const res = await api.post("/content/preview-pdf", {}, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not generate preview"); }
  };

  const createLib = async () => {
    if (!nl.name) return toast.error("Snippet name required");
    const { data } = await api.post("/content-library", { ...nl, kind: "text" });
    setLibrary([data, ...library]);
    setNl({ category: "founder", name: "", html: "" });
    toast.success("Snippet added");
  };
  const delLib = async (lid) => { await api.delete(`/content-library/${lid}`); setLibrary(library.filter((x) => x.id !== lid)); };
  const saveLib = async () => {
    if (!editLib.name.trim()) return toast.error("Snippet name required");
    const { data } = await api.put(`/content-library/${editLib.id}`, { category: editLib.category, name: editLib.name, html: editLib.html });
    setLibrary(library.map((x) => (x.id === editLib.id ? data : x)));
    setEditLib(null); toast.success("Snippet updated");
  };

  const saveTemplate = async (t) => {
    await api.put(`/email-templates/${t.key}`, { subject: t.subject, heading: t.heading, body: t.body, cta_label: t.cta_label });
    toast.success("Template saved");
  };
  const previewTemplate = async (key) => {
    const { data } = await api.post(`/email-templates/${key}/preview`);
    setPreview(data);
  };
  const setTpl = (key, patch) => setTemplates((ts) => ts.map((t) => (t.key === key ? { ...t, ...patch } : t)));

  useEffect(() => {
    if (!content) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/content/preview-pdf", { content }, { responseType: "blob" });
        setLiveUrl((u) => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(res.data); });
      } catch (e) { /* ignore transient */ }
    }, 900);
    return () => clearTimeout(t);
  }, [content]);

  const saveContent = async () => { await api.put("/content", content); toast.success("Proposal content saved"); };
  const previewPdf = async () => {
    try {
      const res = await api.post("/content/preview-pdf", { content }, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Could not generate preview"); }
  };

  const TAB_META = {
    users: { title: "Users & Access", accent: "sky", Icon: Users2 },
    content: { title: "Proposal Templates", accent: "teal", Icon: FileText },
    emails: { title: "Email Templates", accent: "indigo", Icon: FileText },
    loe: { title: "Engagement (LoE)", accent: "violet", Icon: FileText },
    testimonials: { title: "Testimonials", accent: "amber", Icon: Star },
    library: { title: "Content Library", accent: "rose", Icon: Library },
    home: { title: "Login Page", accent: "orange", Icon: LogIn },
    welcome: { title: "Welcome Screen", accent: "emerald", Icon: Sparkles },
    integrations: { title: "Integrations", accent: "indigo", Icon: Plug },
    numbering: { title: "Numbering", accent: "navy", Icon: Hash },
    pipeline: { title: "Pipeline", accent: "fuchsia", Icon: KanbanSquare },
    images: { title: "Image Re-sync", accent: "rose", Icon: ImageOff },
  };

  if (!activeTab) {
    const tiles = [
      { id: "templates-group", to: "/admin/templates", title: "Templates & Letters", desc: "Proposal, email & engagement templates", accent: "teal", Icon: FileText, show: true },
      { id: "users", title: "Users & Access", desc: "Team members, roles & permissions", accent: "sky", Icon: Users2, show: isSuper },
      { id: "testimonials", title: "Testimonials", desc: "Client quotes & logos for proposals", accent: "amber", Icon: Star, show: true },
      { id: "library", title: "Content Library", desc: "Reusable proposal content blocks", accent: "rose", Icon: Library, show: isSuper },
      { id: "home", title: "Login Page", desc: "Branding on the sign-in screen", accent: "orange", Icon: LogIn, show: isSuper },
      { id: "welcome", title: "Welcome Screen", desc: "First-run welcome message", accent: "emerald", Icon: Sparkles, show: isSuper },
      { id: "integrations", title: "Integrations", desc: "Xero & connected services", accent: "indigo", Icon: Plug, show: isSuper },
      { id: "numbering", title: "Numbering", desc: "Proposal reference formats", accent: "navy", Icon: Hash, show: isSuper },
      { id: "pipeline-settings", to: "/admin/pipeline", title: "Sales Pipeline Settings", desc: "Touchpoints, lead sources, stages & win/loss reasons", accent: "fuchsia", Icon: KanbanSquare, show: isSuper },
      { id: "onboarding-questions", to: "/admin/onboarding-questions", title: "Onboarding Questions", desc: "Questions clients answer after signing, per service & entity", accent: "teal", Icon: ClipboardList, show: isSuper },
      { id: "clearance-template", to: "/admin/clearance-template", title: "Clearance Letter", desc: "Master professional clearance letter & merge fields", accent: "indigo", Icon: FileText, show: isSuper },
      { id: "requirement-blocks", to: "/admin/requirement-blocks", title: "Requirement Blocks", desc: "Reusable clearance requirement wording mapped to services", accent: "teal", Icon: ListChecks, show: isSuper },
      { id: "pricing-triggers", to: "/admin/pricing-triggers", title: "Pricing Triggers", desc: "Flag quotes for review or require a consultation", accent: "amber", Icon: AlertTriangle, show: isPricingAdmin },
      { id: "images", title: "Image Re-sync", desc: "Restore images missing after a deployment", accent: "rose", Icon: ImageOff, show: isSuper },
    ].filter((t) => t.show);
    const q = settingsQuery.trim().toLowerCase();
    const visible = q ? tiles.filter((t) => (t.title + " " + t.desc).toLowerCase().includes(q)) : tiles;
    return (
      <div>
        <PageHeader title="Settings" subtitle="Manage templates, users, content and system configuration." accent="navy" Icon={Settings} />
        <div className="p-4 md:p-8 max-w-[1400px]">
          <div className="relative mb-6 max-w-md">
            <Search className="w-4 h-4 text-cass-slate absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={settingsQuery} onChange={(e) => setSettingsQuery(e.target.value)} placeholder="Search settings…" className="pl-9 h-11 rounded-xl" data-testid="settings-search" autoFocus />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="settings-hub">
            {visible.map(({ id, show, ...t }) => (
              <SettingsTile key={id} {...t} onClick={() => (t.to ? navigate(t.to) : setSp({ tab: id }))} />
            ))}
            {!visible.length && <div className="text-sm text-cass-slate py-8" data-testid="settings-no-match">No settings match “{settingsQuery}”.</div>}
          </div>
        </div>
      </div>
    );
  }

  const meta = TAB_META[activeTab] || { title: "Admin", accent: "navy", Icon: Settings };
  return (
    <div>
      <PageHeader title={meta.title} accent={meta.accent} Icon={meta.Icon}
        actions={<Button variant="outline" onClick={() => navigate("/admin")} data-testid="settings-back"><ChevronLeft className="w-4 h-4 mr-1" />Settings</Button>} />
      <div className="p-8">
        <Tabs value={activeTab}>

          {isSuper && (
            <TabsContent value="users"><UsersTab currentUserId={user?.id} /></TabsContent>
          )}

          <TabsContent value="content" className="mt-4">
            <p className="text-sm text-cass-slate mb-4 max-w-2xl">Build every proposal here. Open a template to edit its pages — add content blocks (text, images, sections) to any page, including the cover background image, all in one place.</p>
            <TemplatesManager />
          </TabsContent>

          <TabsContent value="emails" className="mt-4 space-y-6 max-w-3xl">
            <p className="text-sm text-cass-slate">Every automated email in one place, grouped by when it's sent. Click a row to edit its wording. Placeholders like <code className="text-cass-teal">{"{client_name}"}</code> fill in automatically on send.</p>

            {(() => {
              const GROUPS = [
                { id: "send", title: "Sending & chasing", desc: "Sent to the client when a proposal goes out and while it's awaiting signature.", keys: ["proposal_issued", "reminder", "reminder_1", "reminder_2", "reminder_3", "reminder_4", "reminder_5", "expiry"] },
                { id: "signing", title: "Acceptance & signing", desc: "Verification, confirmations and Letter of Engagement nudges.", keys: ["otp", "accepted", "loe_reminder", "loe_signed"] },
                { id: "internal", title: "Internal (CASS team)", desc: "Notifications sent to your team, never the client.", keys: ["internal_accepted", "internal_declined", "internal_renewal_due", "internal_loe_signed"] },
              ];
              const used = new Set(GROUPS.flatMap((g) => g.keys));
              const other = templates.filter((t) => !used.has(t.key));
              if (other.length) GROUPS.push({ id: "other", title: "Other", desc: "", keys: other.map((t) => t.key) });
              const byKey = Object.fromEntries(templates.map((t) => [t.key, t]));

              const emailItem = (t) => (
                <AccordionItem key={t.key} value={t.key} className="border border-border rounded-md px-3" data-testid={`email-${t.key}`}>
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex flex-col items-start text-left">
                      <span className="font-medium text-cass-navy">{t.name}</span>
                      <span className="text-[11px] text-cass-slate font-normal truncate max-w-[46ch]">{t.subject}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-3 pb-4">
                    <div><Label className="text-xs">Subject</Label><Input value={t.subject || ""} onChange={(e) => setTpl(t.key, { subject: e.target.value })} disabled={!isSuper} data-testid={`email-${t.key}-subject`} /></div>
                    <div><Label className="text-xs">Heading</Label><Input value={t.heading || ""} onChange={(e) => setTpl(t.key, { heading: e.target.value })} disabled={!isSuper} /></div>
                    <div><Label className="text-xs">Body</Label><Textarea rows={5} value={t.body || ""} onChange={(e) => setTpl(t.key, { body: e.target.value })} disabled={!isSuper} data-testid={`email-${t.key}-body`} /></div>
                    {"cta_label" in t && (
                      <div><Label className="text-xs">Button label (leave blank to hide the button)</Label><Input value={t.cta_label || ""} onChange={(e) => setTpl(t.key, { cta_label: e.target.value })} disabled={!isSuper} /></div>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {(t.placeholders || []).map((ph) => <Badge key={ph} variant="secondary" className="text-[11px] font-mono">{`{${ph}}`}</Badge>)}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => previewTemplate(t.key)} data-testid={`preview-${t.key}`}>Preview</Button>
                      {isSuper && <Button size="sm" className="bg-cass-navy text-white" onClick={() => saveTemplate(t)} data-testid={`save-email-${t.key}`}>Save</Button>}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );

              return GROUPS.map((g) => {
                const items = g.keys.map((k) => byKey[k]).filter(Boolean);
                if (!items.length) return null;
                return (
                  <section key={g.id} className="space-y-2" data-testid={`email-group-${g.id}`}>
                    <div>
                      <h3 className="font-alt font-semibold text-cass-navy">{g.title}</h3>
                      {g.desc && <p className="text-xs text-cass-slate">{g.desc}</p>}
                    </div>
                    {g.id === "send" && <ReminderScheduleCard canEdit={isSuper} />}
                    <Accordion type="single" collapsible className="space-y-2">
                      {items.map(emailItem)}
                    </Accordion>
                  </section>
                );
              });
            })()}

            <section className="space-y-2" data-testid="email-group-send-templates">
              <div>
                <h3 className="font-alt font-semibold text-cass-navy">Proposal send templates</h3>
                <p className="text-xs text-cass-slate">Named intros the sender can pick from when issuing a proposal.</p>
              </div>
              <ProposalEmailTemplates />
            </section>

            <section className="space-y-2" data-testid="email-group-settings">
              <h3 className="font-alt font-semibold text-cass-navy">Settings</h3>
              <Card className="p-6 rounded-md space-y-3" data-testid="email-logo-card">
                <h4 className="font-alt font-semibold text-cass-navy">Email header logo</h4>
                <p className="text-sm text-cass-slate">Shown on the right of the dark blue bar at the top of every email. Use a white/reversed logo (PNG) so it stands out — it sits on a white chip. Leave unset to use your main brand logo.</p>
                <div className="flex items-center gap-3">
                  {emailLogo && <span className="inline-block bg-cass-navy rounded-md p-2"><img src={`${BK}/api/public/files/${emailLogo}`} alt="email logo" className="h-9 w-auto object-contain" data-testid="email-logo-preview" /></span>}
                  {isSuper && <Button variant="outline" size="sm" onClick={() => setEmailPick(true)} data-testid="email-logo-btn">{emailLogo ? "Change email logo" : "Upload email logo"}</Button>}
                  {isSuper && emailLogo && <Button variant="ghost" size="sm" onClick={async () => { await api.put("/brand", { email_logo_path: "" }); setEmailLogo(""); toast.success("Reset to main brand logo"); }} data-testid="email-logo-remove">Remove</Button>}
                </div>
              </Card>
            </section>
          </TabsContent>

          {isSuper && (
            <TabsContent value="loe" className="mt-4">
              <LoEAdmin />
            </TabsContent>
          )}
          {isSuper && (
            <>
            <TabsContent value="home" className="mt-4">
              <HomeAdmin />
            </TabsContent>
            <TabsContent value="welcome" className="mt-4">
              <WelcomeAdmin />
            </TabsContent>
            </>
          )}

          {isSuper && (
            <TabsContent value="integrations" className="mt-4">
              <XeroCard />
            </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="numbering" className="mt-4 space-y-4">
              <NumberingCard />
              <AnnualUpliftCard canEdit={isSuper} />
            </TabsContent>
          )}

          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="library"><LibraryTab /></TabsContent>
          {isSuper && <TabsContent value="images"><ImageResyncTab /></TabsContent>}
        </Tabs>
      </div>

      <ImagePicker open={emailPick} onOpenChange={setEmailPick} onSelect={async (path) => { try { await api.put("/brand", { email_logo_path: path }); setEmailLogo(path); setEmailPick(false); toast.success("Email logo updated"); } catch { toast.error("Could not update"); } }} />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="email-preview-dialog">
          <DialogHeader><DialogTitle>Preview — {preview?.subject}</DialogTitle></DialogHeader>
          {preview && <iframe title="email-preview" srcDoc={preview.html} className="w-full h-[520px] border border-border rounded-md bg-white" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}


